import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Camera } from "../../../models/camera.model";
import { Observable } from 'rxjs';
import { ConfigCameras } from '../../../models/configcamera.model';
import { AbCameras } from 'app/abstract/full-pages/cameras/cameras.abstract';
import {toBinaryStr} from "app/commons/toBinaryStr";
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class CamerasService extends AbCameras {
  private controllerDigifort: AbortController | null = null;

  constructor(http: HttpClient, private sanitizer: DomSanitizer) {
    super(http);
  }

  private controllerDigfort: AbortController | null = null;

  public getAllCameras(): Observable<any> {
    return this.get("cameras/all");
  }

  public getAllCamerasSemCFTV(): Observable<any> {
    return this.get("cameras/all/not_cftv");
  }

  public findAllCameras(): Observable<any> {
    return this.get("cameras/alldto");
  }

  public findCftvAllCameras(id: number): Observable<any> {
    return this.get("cameras/alldto/" + id);
  }

  public restauraCameraDeletada(id: number): Observable<any> {
    return this.get("cameras/restauraCameraDeletada/" + id);
  }

  public findAllBySistemaVms(sistemavms: string): Observable<any> {
    return this.get(`cameras/sistemavms/${sistemavms}`);
  }

  public saveCameras(cameras: Camera[]): Observable<any> {
    return this.save("cameras/savelist/", cameras);
  }

  public saveCamerasGrupos(cameras: Camera[]): Observable<any> {
    return this.save("cameras/savelistgruposcameras/", cameras);
  }

  public saveCamerasLPR(cameras: Camera[]): Observable<any> {
    return this.save("cameras/savelprcameras/", cameras);
  }

  deletar(id: number) {
    return this.delete("cameras/", id);
  }

  deletarTodos(camerasIds: number[]) {
    return this.deleteComBody("cameras/delete/all", camerasIds);
  }

  public getImagemCamera(config: ConfigCameras, cameraId: string, sessionId: string): Observable<any> {
    const headers = this.getHeaders(sessionId, config);
    const url = `${config.servidor}:${config.porta}/camera.cgi?resolucao=320x240&amp;qualidade=50&amp;formato=jpg&amp;canrepeatimage=true;camera=${cameraId}`;
    return this.http.get(url, { headers, observe: 'response', responseType: "blob" });
  }

  public getImagemCameraPrincipal(params: any, sessionId: string): Observable<any> {
    const headers = this.getHeaders(sessionId, params);
    return this.http.get(params.url, { headers, observe: 'response', responseType: 'blob' });
  }

  public getImagemCameraDguardNovaAPI(config: ConfigCameras, cameraId: string): Observable<string> {
    return new Observable<string>((subscriber) => {
      const DEFAULT_SERVER_GUID = "{EF36C93C-DBE3-4369-94C1-30D2AC44A6D9}";
      const urlDGuardNovaAPI = `${config.servidor}:${config.porta}/api/servers/${DEFAULT_SERVER_GUID}/cameras/${cameraId}/image.jpg`;
      const tokenDGuardNovaAPI = config.token;

      fetch(urlDGuardNovaAPI, {
        headers: {
          Authorization: `Bearer ${tokenDGuardNovaAPI}`, 'Cache-Control': 'no-store'
        },
        cache: 'no-store'
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          return res.blob();
        })
        .then((blob) => {
          const objectURL = URL.createObjectURL(blob);
          subscriber.next(objectURL);
          subscriber.complete();
        })
        .catch((err) => {
          console.error('Erro ao buscar imagem da câmera:', err);
          subscriber.error(err);
        });
    });
  }

  public getHeaders(sessionId, params): HttpHeaders {
    const headers = new HttpHeaders();
    if (sessionId)
      return headers.set("SessaoId", sessionId)

    return headers.set("Authorization", 'Basic ' + btoa(toBinaryStr(`${params.login}:${params.senha}`)))
  }

  public getImagemSigMainThumbnailJpg(
    token: string,
    servidorCamera: string,
    codigoCamera: string
  ): Observable<any> {
    return this.http.get(
      `${servidorCamera}partner/channels/${codigoCamera}/thumbnail.jpg`,
      {
        headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
        observe: 'response',
        responseType: 'blob',
      }
    );
  }

  public getImagemSigMainPlaylist(
    token: string,
    servidorCamera: string,
    codigoCamera: string
  ): Observable<any> {
    return this.http.get(
      `${servidorCamera}partner/channels/${codigoCamera}/url`,
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${token}`,
        }),
      }
    );
  }

  getStreamUrlDigfort(params): Promise<string> {
    if (this.controllerDigfort) {
      this.controllerDigfort.abort();
    }
    this.controllerDigfort = new AbortController();
    const { signal } = this.controllerDigfort;

    const headers = new Headers({
      'Authorization': 'Basic ' + btoa(toBinaryStr(`${params.login}:${params.senha}`))
    });

    return fetch(params.url, { headers: headers, signal: signal })
      .then((response) => {
        if (response.ok) {
          return params.url;
        } else {
          return 'error';
        }
      })
      .catch((error) => {
        return error.code == 20 ? '' : 'error';
      });
  }

  public getSnapshotDigfort(params: any): Promise<string> {
    const headers = new Headers({
      'Authorization': 'Basic ' + btoa(toBinaryStr(`${params.login}:${params.senha}`))
    });

    return fetch(params.url, { headers: headers })
      .then((response) => {
        if (response.ok) {
          return response.blob().then((blob) => URL.createObjectURL(blob));
        } else {
          throw new Error(
            'Erro na resposta da requisição: ' + response.statusText
          );
        }
      })
      .catch((error) => {
        console.error('Erro ao fazer a requisição:', error);
        throw error;
      });
  }

  public getStreamReaderDigfort(params: any): Promise<ReadableStreamDefaultReader<Uint8Array>> {
    this.controllerDigifort = new AbortController();
    const { signal } = this.controllerDigifort;

    const headers = new Headers({
      Authorization: 'Basic ' + btoa(`${params.login}:${params.senha}`),
    });

    return fetch(params.url, { headers: headers, signal })
      .then((response) => {
        if (response.ok && response.body) {
          return response.body.getReader();
        } else {
          throw new Error(
            'Erro na resposta da requisição: ' + response.statusText
          );
        }
      })
      .catch((error) => {
        throw error;
      });
  }

  cancelStreamDigfort(): void {
    if (this.controllerDigifort) {
      this.controllerDigifort.abort();
    }
  }

}
